import React, {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Button, message, Pagination, Table, Select, Divider } from "antd";
import { Splitter } from "antd";
import { CloseOutlined, FormOutlined } from "@ant-design/icons";
import CommonTopDetails from "../../CommonTopDetails";
import useCommonListing from "../../customHooks/useCommonListing";

import { GetFormattedDateTime } from "../../../utils/DateTimeUtils";
import axiosInstance from "../../../../api/Axios";

const LanguageAssign = forwardRef(
  (
    {
      open,
      onClose,
      userInfo,
      initialSelectedRowKeys,
      initialCurrentPage,
      initialPageSize,
      initialUrl,
      fullscreen,
      setFullscreen,
      loadAgain,
    },
    ref
  ) => {
    const [selectedRowKeys1, setSelectedRowKeys1] = useState([]);
    const [language, setLanguage] = useState(null);
    const columns = [
      {
        title: "Course ID",
        dataIndex: "course_id",
        key: "course_id",
        width: 100,
      },
      {
        title: "Course Title",
        dataIndex: "name",
        key: "name",
        ellipsis: !fullscreen,
        width: 295,
      },
      {
        title: "Language",
        dataIndex: "language",
        key: "language",
        width: 120,
      },
      { title: "Course Mode", dataIndex: "mode", key: "mode", width: 110 },
      ...(fullscreen
        ? [
            {
              title: "Category",
              dataIndex: "category",
              key: "category",
              width: 160,
            },

            {
              title: "Created At",
              dataIndex: "created_at",
              key: "created_at",
              width: 120,
              render: GetFormattedDateTime,
            },
          ]
        : []),
    ];

    const [buttonLoading, setButtonLoading] = useState(false);

    const {
      data,
      itemsLength,
      loading,
      filterData,
      onShowSizeChange,
      onPageChange,
      handleSearchChange,
      setFilterData,
      // handleSwitchChange,
      // handleClearAll,
      // setFilterUrl,
    } = useCommonListing({
      userInfo,
      passSize: initialSelectedRowKeys?.length > 0 ? initialPageSize : 5,
      passPage: initialSelectedRowKeys?.length > 0 ? initialCurrentPage : 1,
      passURL: `/courselist/?organization=${userInfo?.data?.organization?.id}&mode=E-Learning`,
      modalOpen: open,
    });
    useEffect(() => {
      if (initialSelectedRowKeys?.length > 0 && open) {
        setSelectedRowKeys1(initialSelectedRowKeys || []);
        setFilterData((prevData) => ({
          ...prevData,
          currentPage: initialCurrentPage || 1,
          pageSize: initialPageSize || 5,
          filter_url: initialUrl || "",
        }));
      }
    }, [
      initialSelectedRowKeys,
      setFilterData,
      initialCurrentPage,
      initialPageSize,
      initialUrl,
      open,
    ]);
    // eslint-disable-next-line no-unused-vars
    const { pageSize, currentPage, active, filter_url } = filterData;

    const rowSelection1 = {
      selectedRowKeys: selectedRowKeys1,
      onChange: (newSelectedKeys, selectedRows) => {
        // Get all keys visible on current page
        const currentPageKeys = data.map((row) => row.id);

        // Create a Set from current selections
        const updatedKeysSet = new Set(selectedRowKeys1);

        // Remove any deselected keys from current page
        currentPageKeys.forEach((key) => {
          updatedKeysSet.delete(key);
        });

        // Add all newly selected keys
        newSelectedKeys.forEach((key) => {
          updatedKeysSet.add(key);
        });

        // Final merged keys
        const finalKeys = Array.from(updatedKeysSet);
        setSelectedRowKeys1(finalKeys);
      },
    };
    function getCookie(name) {
      const cookies = document.cookie.split("; ");
      for (let i = 0; i < cookies.length; i++) {
        const [cookieName, cookieValue] = cookies[i].split("=");
        if (cookieName === name) {
          return cookieValue;
        }
      }
      return null;
    }
    const csrfToken = getCookie("csrftoken");

    useImperativeHandle(ref, () => ({
      handleCloseChild,
    }));

    const handleCloseChild = () => {
      setFullscreen(false);
      setSelectedRowKeys1([]);
      setLanguage(null);
      onClose();
    };

    const submitData = async () => {
      setButtonLoading(true);

      try {
        await axiosInstance.post(
          "/courseorganizationbulkassign/",
          {
            course_ids: selectedRowKeys1,
            language: language,
            type: "language_assign",
          },
          {
            headers: {
              "X-CSRFToken": csrfToken,
            },
          }
        );
        message.success("Bulk assignment successful!");
      } catch (error) {
        message.error("Bulk assignment failed. Please try again.");
      } finally {
        setButtonLoading(false);
        loadAgain();
        handleCloseChild();
      }
    };
    const languageOptions = [
      { value: "English", label: "English" },
      { value: "Spanish", label: "Spanish" },
      { value: "French", label: "French" },
      { value: "Portuguese", label: "Portuguese" },
    ];
    return (
      <div
        style={{
          width: "100%",
          overflow: "hidden",
        }}
      >
        <Splitter style={{ width: "100%" }}>
          <Splitter.Panel
            defaultSize="100%"
            min="100%"
            max="100%"
            className="table-wrapper"
            style={{ overflowX: "auto", overflowY: "hidden" }}
          >
            <CommonTopDetails
              title={active ? "Courses" : "Inactive Courses"}
              titleSuffix={`${itemsLength || 0} course(s) ${
                selectedRowKeys1.length > 0
                  ? `| selected ${selectedRowKeys1.length} course(s)`
                  : ""
              }`}
              searchPlaceholder="Search by ID or Title"
              searchAction={handleSearchChange}
              marginPass="15px"
              paddingInlinepass="10px"
              showAddButton={false}
              filterAction={false}
              filterUrl={false}
            />
            <Table
              columns={columns}
              dataSource={data}
              size="small"
              bordered
              rowKey="id"
              className="table-container"
              scroll={{
                x: 200,
                y: fullscreen ? 500 : 400,
              }}
              loading={loading}
              style={{ marginInline: "1rem" }}
              pagination={false}
              rowSelection={rowSelection1}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                padding: "1rem",
              }}
            >
              <Pagination
                size="small"
                showSizeChanger
                onShowSizeChange={onShowSizeChange}
                onChange={onPageChange}
                pageSizeOptions={["5", "10", "15", "30", "50", "100"]}
                current={currentPage}
                defaultPageSize={pageSize}
                total={itemsLength}
              />
            </div>
          </Splitter.Panel>
        </Splitter>

        <div style={{ marginTop: 22 }}>
          <Divider />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 16,
              gap: 16,
            }}
          >
            <span style={{ fontWeight: 500, fontSize: 16, marginRight: 12 }}>
              Select the language
            </span>
            <Select
              style={{ width: 180 }}
              placeholder="Select Language"
              value={language}
              onChange={setLanguage}
              options={languageOptions}
            />
          </div>
          <Divider />
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              margin: "3rem",
              marginInline: "2rem",
              marginBottom: "1rem",
            }}
          >
            <Button
              key="back"
              onClick={(e) => {
                e.stopPropagation();
                handleCloseChild();
              }}
              style={{ marginRight: "1rem" }}
            >
              <CloseOutlined />
              Cancel
            </Button>

            <Button
              type="primary"
              htmlType="submit"
              onClick={submitData}
              loading={buttonLoading}
              disabled={selectedRowKeys1.length === 0 || !language}
            >
              Save Assignment{selectedRowKeys1.length > 1 ? "s" : ""}
              <FormOutlined />
            </Button>
          </div>
        </div>
      </div>
    );
  }
);

export default LanguageAssign;
